import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton'; // assuming you have shadcn/ui
import { Minus, Plus, ShoppingCart, AlertCircle } from 'lucide-react';
import { gql, useQuery } from '@apollo/client';
import { fileUrl } from '../lib/utils';
import { useCart } from '../contexts/CartContext';

const PRODUCT = gql`
  query ProductDetail($productDetailId: ID!) {
    productDetail(id: $productDetailId) {
      id
      name
      code
      thumbnail
      images
      video
      attachment
      price
      discountPrice
      isSalonExclusive
      salonDiscountPercent
      status
      discount
      description
      ingredients
      favorite
      skinTypes
      productLines
      productTypes
    }
  }
`;

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [quantity, setQuantity] = useState(1);

  const [selectedImage, setSelectedImage] = useState(0);
  const { addToCart } = useCart();
  const { data, loading, error } = useQuery(PRODUCT, {
    variables: { productDetailId: id },
    skip: !id,
  });

  const product = data?.productDetail;

  // Loading State

  const handleAddToCart = () => {
    addToCart(product?.id, quantity);
  };

  if (loading) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <div className='grid lg:grid-cols-2 gap-12'>
          {/* Image Gallery Skeleton */}
          <div>
            <Skeleton className='w-full h-96 mb-4 rounded-lg' />
            <div className='grid grid-cols-3 gap-4'>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className='h-32 rounded-sm' />
              ))}
            </div>
          </div>

          {/* Info Skeleton */}
          <div className='space-y-6'>
            <div>
              <Skeleton className='h-10 w-96 mb-2' />
              <Skeleton className='h-5 w-32' />
            </div>
            <Skeleton className='h-20 w-full' />
            <Skeleton className='h-16 w-full' />
            <div className='flex gap-4'>
              <Skeleton className='h-12 w-40' />
              <Skeleton className='h-12 flex-1' />
            </div>
            <div className='space-y-4'>
              <Skeleton className='h-6 w-32' />
              <Skeleton className='h-32 w-full' />
              <Skeleton className='h-6 w-32' />
              <Skeleton className='h-24 w-full' />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !product) {
    return (
      <div className='container mx-auto px-4 py-16 text-center'>
        <Card className='max-w-md mx-auto p-8'>
          <AlertCircle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h2 className='text-xl font-semibold mb-2'>Бүтээгдэхүүн олдсонгүй</h2>
          <p className='text-muted-foreground'>
            {error
              ? 'Серверт алдаа гарлаа. Дахин оролдоно уу.'
              : 'Энэ ID-тай бүтээгдэхүүн байхгүй байна.'}
          </p>
          <Button className='mt-6' onClick={() => window.location.reload()}>
            Дахин ачаалах
          </Button>
        </Card>
      </div>
    );
  }

  // Format price with Mongolian tögrög
  const formattedPrice = new Intl.NumberFormat('mn-MN').format(product.price);
  const formattedDiscountPrice = product.discountPrice
    ? new Intl.NumberFormat('mn-MN').format(product.discountPrice)
    : null;

  const displayImages =
    product.images && product.images.length > 0
      ? product.images
      : [product.thumbnail || '/placeholder.png'];

  return (
    <div className='container mx-auto px-4 py-8'>
      <div className='grid lg:grid-cols-2 gap-12'>
        {/* Product Images */}
        <div className='space-y-4'>
          <div className='bg-muted rounded-lg overflow-hidden'>
            <img
              src={fileUrl(displayImages[selectedImage])}
              alt={product.name}
              className='w-full h-auto object-cover'
            />
          </div>

          {displayImages.length > 1 && (
            <div className='grid grid-cols-4 gap-3'>
              {displayImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`border-2 rounded-md overflow-hidden transition-all ${
                    selectedImage === index
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <img
                    src={fileUrl(image)}
                    alt={`${product.name} - зураг ${index + 1}`}
                    className='w-full h-24 object-cover'
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className='space-y-8'>
          <div>
            <h1 className='text-3xl md:text-4xl font-light mb-2'>
              {product.name}
            </h1>
            <p className='text-sm text-muted-foreground'>
              SKU: {product.code || 'Код байхгүй'}
            </p>
          </div>

          <div className='border-t border-b border-border py-6'>
            <div className='flex flex-wrap items-end gap-4'>
              {formattedDiscountPrice ? (
                <>
                  <p className='text-4xl font-bold text-primary'>
                    {formattedDiscountPrice}₮
                  </p>
                  <p className='text-2xl text-muted-foreground line-through'>
                    {formattedPrice}₮
                  </p>
                  {product.discount && (
                    <span className='bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium'>
                      -{product.discount}%
                    </span>
                  )}
                </>
              ) : (
                <p className='text-4xl font-bold text-primary'>
                  {formattedPrice}₮
                </p>
              )}
              {product.isSalonExclusive && product.salonDiscountPercent && (
                <span className='bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium'>
                  -{Math.round(product.salonDiscountPercent)}%
                </span>
              )}
            </div>
          </div>

          {product.description && (
            <p className='text-muted-foreground leading-relaxed'>
              {product.description}
            </p>
          )}

          {/* Quantity & Add to Cart */}
          <div className='flex items-center gap-4'>
            <div className='flex items-center border border-border rounded-md'>
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className='p-3 hover:bg-muted transition-colors'
                aria-label='Багаа хасах'
              >
                <Minus className='h-4 w-4' />
              </button>
              <span className='px-6 py-3 min-w-20 text-center font-medium'>
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className='p-3 hover:bg-muted transition-colors'
                aria-label='Нэмэх'
              >
                <Plus className='h-4 w-4' />
              </button>
            </div>

            <Button
              size='lg'
              className='flex-1 text-lg font-medium'
              disabled={product.status !== 'active'}
              onClick={handleAddToCart}
            >
              <ShoppingCart className='mr-2 h-5 w-5' />
              {product.status === 'active'
                ? 'Сагсанд хийх'
                : 'Зарагдаж дууссан'}
            </Button>
          </div>

          {/* Tabs: Ingredients, Usage, etc. */}
          <div className='space-y-8 pt-6 border-t border-border'>
            {product.ingredients && (
              <div>
                <h3 className='font-semibold text-lg mb-3'>Найрлага</h3>
                <p className='text-sm text-muted-foreground leading-relaxed font-mono text-xs'>
                  {product.ingredients}
                </p>
              </div>
            )}

            {product.skinTypes?.length > 0 && (
              <div>
                <h3 className='font-semibold text-lg mb-3'>Арьсны төрөл</h3>
                <div className='flex flex-wrap gap-2'>
                  {product.skinTypes.map((type: string) => (
                    <span
                      key={type}
                      className='px-3 py-1 bg-primary/10 text-primary rounded-full text-sm'
                    >
                      {type
                        .toLowerCase()
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (char) => char.toUpperCase())}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {product.productLines?.length > 0 && (
              <div>
                <h3 className='font-semibold text-lg mb-3'>Aнгилал</h3>
                <div className='flex flex-wrap gap-2'>
                  {product.productLines.map((line: string) => (
                    <span key={line} className='text-sm text-muted-foreground'>
                      {line
                        .toLowerCase()
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (char) => char.toUpperCase())}
                      {line !==
                        product.productLines[product.productLines.length - 1] &&
                        ', '}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related Products Section */}
      <section className='mt-20'>
        <h2 className='text-3xl font-light text-center mb-10'>
          Ижил төстэй бүтээгдэхүүнүүд
        </h2>
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6'>
          {/* You can add a RelatedProducts component here later */}
          {/* <RelatedProducts productId={product.id} /> */}
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
